// COPIED from ptc-core web/src/feedback/feedbackContext.ts @ de39fff (fleet feedback contract v1, 2026-10-21);
// `uiLanguage` added per contract v1.1 (C4 = eight keys, ptc-core#354, ruled 2026-10-27).
// Changes from the source: `buildSha` is supervision's own build stamp (BUILD_COMMIT from src/buildInfo.ts, the
// full commit the bundle was built from, also what verify-deploy reads) instead of core's BUILD_ID. Conformance: src/components/feedback/FeedbackWidgetConformance.test.tsx.
/**
 * The context a feedback report carries automatically (feedback-loop spec §3.2) — shown to the
 * reporter before it is sent, because telemetry a person cannot see erodes trust in exactly the
 * people this feature is for.
 *
 * ⚠ THIS IS A DATA EGRESS POINT, like `reportClientError.ts`. Two rules differ from that module
 * on purpose, and the server enforces both again (`FeedbackContentGuard`):
 *   - the ROUTE keeps its query string, because filter and view state live there (filter contract
 *     v2 §11) and the developer must open exactly what the reporter saw — BUT free-text search
 *     parameters are stripped: `?q=John+Smith` is a client's name, not view state;
 *   - recent errors come from the SAME ring `reportClientError` already keeps (BL-042: no second
 *     capture path), so they carry only that module's PII-free shape.
 */
import { recentClientErrors } from '../telemetry/reportClientError';
import { BUILD_COMMIT } from '../buildInfo';
import i18n from '../i18n';

/** Query parameters that carry typed text rather than view state. */
export const FREE_TEXT_PARAMS = ['q', 'search', 'query', 'term'] as const;

export interface FeedbackRecentError {
  name: string;
  message: string;
  pathname: string;
  timestamp: string;
}

export interface FeedbackContext {
  route: string;
  buildSha: string;
  viewport: string;
  userAgent: string;
  clientTime: string;
  tz: string;
  /** The language the UI is RENDERING in (the i18n instance's resolved language), not the browser's: a
   *  "this word is wrong" report is about what the reporter was reading (contract v1.1 C4). */
  uiLanguage: string;
  recentErrors: FeedbackRecentError[];
}

/** `pathname + search`, minus every free-text parameter. */
export function routeWithoutFreeText(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if ((FREE_TEXT_PARAMS as readonly string[]).includes(key.toLowerCase())) params.delete(key);
  }
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

export function buildFeedbackContext(now: Date = new Date()): FeedbackContext {
  return {
    route: routeWithoutFreeText(window.location.pathname, window.location.search),
    // buildInfo.ts: null is the honest answer outside a deploy build, rendered as "unknown", never substituted.
    buildSha: BUILD_COMMIT ?? 'unknown',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    clientTime: now.toISOString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'unknown',
    uiLanguage: i18n.resolvedLanguage ?? i18n.language ?? 'unknown',
    recentErrors: recentClientErrors().map(({ name, message, pathname, timestamp }) => ({
      name,
      message,
      pathname,
      timestamp,
    })),
  };
}
